import React, { useState } from 'react';
import {
  AlertBanner,
  AlertBannerProvider,
  Button,
  Icon,
} from '@saviynt/design-system';

import { COMPONENT_TEXT } from '../../constants/attributeMappingConstants';
import {
  type AttributeMappingProps,
  type GenericAttributeMapping,
} from '../../data-layer/types';
import useAttributeMapping from '../../hooks/useAttributeMapping';
import AttributeMappingHeader from '../AttributeMappingHeader/AttributeMappingHeader';
import AttributeRow from '../AttributeRow/AttributeRow';

import './AttributeMapping.css';

/**
 * Main AttributeMapping component
 */
function AttributeMapping({
  config,
  initialMappings,
  mandatoryMappings,
  onMappingsChange,
  onSave,
  disabled = false,
  showSaveButton = true,
  className = '',
}: AttributeMappingProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [alertBanner, setAlertBanner] = useState<{
    kind: 'Success' | 'Critical';
    title: string;
    description: string;
    visible: boolean;
  }>({ kind: 'Success', title: '', description: '', visible: false });

  const {
    mappings,
    isDirty,
    canAddMoreRows,
    availableSaviyntFields,
    handleAddMapping,
    handleDeleteMapping,
    handleUpdateMapping,
    validateMappings,
  } = useAttributeMapping(
    config,
    initialMappings,
    onMappingsChange,
    mandatoryMappings
  );

  // State for tracking blur events
  const [blurredFields, setBlurredFields] = useState<Set<string>>(new Set());

  // Get validation errors for each mapping - only show error on second occurrence and after blur
  const getValidationError = (mapping: GenericAttributeMapping) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument
    if (!mapping.externalAppField.trim() || !blurredFields.has(mapping.id)) {
      return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    const duplicateIndex = mappings.findIndex(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      (m: GenericAttributeMapping) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        m.externalAppField.trim().toLowerCase() ===
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        mapping.externalAppField.trim().toLowerCase()
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const currentIndex = mappings.findIndex(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (m: GenericAttributeMapping) => m.id === mapping.id
    );

    // Only show error if this is not the first occurrence
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return duplicateIndex !== currentIndex && duplicateIndex !== -1
      ? `${String(mapping.externalAppField)} already exists`
      : undefined;
  };

  // Handle blur event
  const handleFieldBlur = (mappingId: string) => {
    setBlurredFields((prev) => new Set([...prev, mappingId]));
  };

  // Check if we can save
  const canSave =
    isDirty &&
    Array.isArray(mappings) &&
    mappings.length > 0 &&
    mappings.every(
      (mapping: { saviyntField: string; externalAppField: string }) =>
        typeof mapping.saviyntField === 'string' &&
        typeof mapping.externalAppField === 'string' &&
        mapping.saviyntField.trim() &&
        mapping.externalAppField.trim()
    );

  // Handle save
  const handleSave = async () => {
    if (!onSave || !canSave) {
      return;
    }

    // Validate mappings
    const validation = validateMappings() as {
      isValid: boolean;
      errors: string[];
    };

    if (!validation.isValid) {
      setAlertBanner({
        kind: 'Critical',
        title: 'Validation Error',
        description: validation.errors.join(', '),
        visible: true,
      });

      return;
    }

    setIsSaving(true);

    try {
      await onSave(mappings);
      setAlertBanner({
        kind: 'Success',
        title: 'Success',
        description: 'Mappings saved successfully',
        visible: true,
      });
    } catch (error) {
      setAlertBanner({
        kind: 'Critical',
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to save mappings',
        visible: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Close alert banner
  const closeAlertBanner = () => {
    setAlertBanner((prev) => ({ ...prev, visible: false }));
  };

  return (
    <div className={`attribute-mapping-container ${className}`}>
      {/* Alert Banner */}
      <AlertBannerProvider>
        <AlertBanner
          colorTheme={alertBanner.kind}
          title={alertBanner.title}
          description={alertBanner.description}
          isVisible={alertBanner.visible}
          onCancel={closeAlertBanner}
          className='attribute-mapping-alert-banner'
        />
      </AlertBannerProvider>

      {/* Header */}
      <AttributeMappingHeader config={config} />

      {/* Mapping Rows */}
      <div className='attribute-mapping-rows'>
        {mappings.map((row) => (
          <AttributeRow
            key={row.id}
            row={row}
            config={config}
            availableSaviyntFields={availableSaviyntFields}
            onUpdate={handleUpdateMapping}
            onDelete={handleDeleteMapping}
            disabled={disabled}
            canDelete={mappings.length > 1}
            validationError={getValidationError(row)}
            onBlur={() => handleFieldBlur(row.id)}
          />
        ))}
      </div>

      {/* Actions */}
      <div className='attribute-mapping-actions'>
        <Button
          kind='ghost'
          size='large'
          leftIcon={<Icon kind='Add' />}
          onClick={handleAddMapping}
          isDisabled={!canAddMoreRows || disabled}
          aria-label={`Add new mapping (${mappings.length}/${config.saviyntApp.fields.length} used)`}>
          {COMPONENT_TEXT.ADD_MAPPING}
        </Button>
      </div>

      {/* Footer with Save Button */}
      {showSaveButton && (
        <div className='attribute-mapping-footer'>
          <Button
            kind='filled'
            onClick={handleSave}
            isDisabled={!canSave || disabled || isSaving}
            isLoading={isSaving}
            loaderText='Saving...'>
            {COMPONENT_TEXT.SAVE_MAPPINGS}
          </Button>
        </div>
      )}
    </div>
  );
}

export default AttributeMapping;
